import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Chip,
  IconButton,
  Collapse,
  Autocomplete,
} from '@mui/material';
import {
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  Search
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AuditLogFilters, AuditAction } from '../../types/Audit.types';
import { DataService } from '../../services/data-service';
import { LocalizationProvider, DatePicker } from '@mui/lab';

interface AuditLogFiltersProps {
  filters: AuditLogFilters;
  onFiltersChange: (filters: AuditLogFilters) => void;
  onApply: () => void;
  onClear: () => void;
  collapsed?: boolean;
}

interface FilterOptions {
  entityTypes: string[];
  users: Array<{ id: string; name: string }>;
  actions: Array<{ value: AuditAction; label: string }>;
}

const AuditLogFiltersComponent: React.FC<AuditLogFiltersProps> = ({
  filters,
  onFiltersChange,
  onApply,
  onClear,
  collapsed = false
}) => {
  const [expanded, setExpanded] = useState(!collapsed);
  const [options, setOptions] = useState<FilterOptions>({
    entityTypes: [],
    users: [],
    actions: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    setLoading(true);
    try {
      // Load entity types
      const entityTypesResponse = await DataService.get('api/audit/entity-types');
      const entityTypes = entityTypesResponse.ok ? await entityTypesResponse.json() : [];

      // Load users
      const usersResponse = await DataService.get('api/audit/users');
      const users = usersResponse.ok ? await usersResponse.json() : [];

      // Create actions array
      const actions = Object.entries(AuditAction)
        .filter(([key, value]) => typeof value === 'number')
        .map(([key, value]) => ({
          value: value as AuditAction,
          label: key
        }));

      setOptions({
        entityTypes,
        users,
        actions
      });
    } catch (error) {
      console.error('Failed to load filter options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof AuditLogFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => 
      value !== undefined && value !== '' && value !== null
    ).length;
  };

  const getActiveFilters = () => {
    const active: Array<{ key: string; label: string; value: any }> = [];
    
    if (filters.entityType) {
      active.push({
        key: 'entityType',
        label: 'Entity Type',
        value: filters.entityType
      });
    }
    
    if (filters.action !== undefined) {
      const actionLabel = options.actions.find(a => a.value === filters.action)?.label;
      active.push({
        key: 'action',
        label: 'Action',
        value: actionLabel || filters.action
      });
    }
    
    if (filters.userId) {
      const user = options.users.find(u => u.id === filters.userId);
      active.push({
        key: 'userId',
        label: 'User',
        value: user?.name || filters.userId
      });
    }
    
    if (filters.dateFrom) {
      active.push({
        key: 'dateFrom',
        label: 'From Date',
        value: new Date(filters.dateFrom).toLocaleDateString()
      });
    }
    
    if (filters.dateTo) {
      active.push({
        key: 'dateTo',
        label: 'To Date',
        value: new Date(filters.dateTo).toLocaleDateString()
      });
    }
    
    if (filters.searchTerm) {
      active.push({
        key: 'searchTerm',
        label: 'Search',
        value: filters.searchTerm
      });
    }

    return active;
  };

  const removeFilter = (key: string) => {
    handleFilterChange(key as keyof AuditLogFilters, undefined);
  };

  const clearAllFilters = () => {
    onFiltersChange({});
    onClear();
  };

  const hasActiveFilters = getActiveFilters().length > 0;

  return (
    <Card>
      <CardHeader
        title="Filters"
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {hasActiveFilters && (
              <Chip
                label={`${getActiveFiltersCount()} active`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>
        }
      />
      
      <Collapse in={expanded}>
        <CardContent>
          <Stack spacing={3}>
            {/* Search Term */}
            <TextField
              fullWidth
              label="Search"
              placeholder="Search in descriptions, user names..."
              value={filters.searchTerm || ''}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />

            {/* Entity Type and Action */}
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={filters.entityType || ''}
                  label="Entity Type"
                  onChange={(e) => handleFilterChange('entityType', e.target.value || undefined)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {options.entityTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Action</InputLabel>
                <Select
                  value={filters.action !== undefined ? filters.action : ''}
                  label="Action"
                  onChange={(e) => handleFilterChange('action', e.target.value === '' ? undefined : e.target.value)}
                >
                  <MenuItem value="">All Actions</MenuItem>
                  {options.actions.map(action => (
                    <MenuItem key={action.value} value={action.value}>
                      {action.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* User Selection */}
            <Autocomplete
              options={options.users}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
              value={options.users.find(u => u.id === filters.userId) || null}
              onChange={(_, value) => {
                if (typeof value === 'string') {
                  handleFilterChange('userId', value);
                } else {
                  handleFilterChange('userId', value?.id);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="User"
                  placeholder="Select user..."
                />
              )}
            />

            {/* Date Range */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction="row" spacing={2}>
                <DatePicker
                  label="From Date"
                  value={filters.dateFrom ? new Date(filters.dateFrom) : null}
                  onChange={(date) => handleFilterChange('dateFrom', date?.toISOString().split('T')[0])}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <DatePicker
                  label="To Date"
                  value={filters.dateTo ? new Date(filters.dateTo) : null}
                  onChange={(date) => handleFilterChange('dateTo', date?.toISOString().split('T')[0])}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Stack>
            </LocalizationProvider>

            {/* Entity ID */}
            <TextField
              fullWidth
              label="Entity ID"
              placeholder="Filter by specific entity ID..."
              value={filters.entityId || ''}
              onChange={(e) => handleFilterChange('entityId', e.target.value || undefined)}
            />

            {/* Active Filters */}
            {hasActiveFilters && (
              <Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {getActiveFilters().map((filter) => (
                    <Chip
                      key={filter.key}
                      label={`${filter.label}: ${filter.value}`}
                      onDelete={() => removeFilter(filter.key)}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={clearAllFilters}
                disabled={!hasActiveFilters}
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                startIcon={<FilterList />}
                onClick={onApply}
              >
                Apply Filters
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default AuditLogFiltersComponent;